<template>
	<v-card v-if="domainStore.hasAccessRights(UserRole.Editor)" :loading="gptSuggestionsStore.currentlyProcessing > 0">
		<v-container
			fluid
			style="background: rgb(var(--v-theme-contained)); z-index: 1000; width: calc(100% - 2px); margin-right: 2px"
		>
			<v-textarea
				v-model="messageText"
				style="width: 100%; margin: 0 auto"
				variant="solo"
				:disabled="!sessionStore.session || workspacesStore.isOpenedWorkspaceLocked"
				:placeholder="
					sessionStore.session
						? symbolsStore.GUILabel('9c084d37-9c17-4706-afb1-2c001752792e')
						: symbolsStore.GUILabel('8ff020dd-f08d-4262-924d-1afc48a67c2f')
				"
				:rows="1"
				max-rows="5"
				append-inner-icon="mdi-send"
				no-resize
				auto-grow
				hide-details
				@keydown.enter="onEnter"
				@click:append-inner="sendMessageText"
			>
				<template #prepend>
					<div @click="() => (messageUsePrevLang = !messageUsePrevLang)" style="background: #00000001">
						<div
							v-if="messageUsePrevLang && prevLang"
							style="height: 24px; width: 24px; font-size: 24px; margin-top: -6px"
						>
							<Flag :lang="prevLang" />
						</div>
						<v-icon v-if="!messageUsePrevLang || !prevLang" icon="mdi-translate"></v-icon>
					</div>
				</template>
				<template #append v-if="!displayResponsive">
					<v-btn
						:icon="audioStore.isRecording ? 'mdi-microphone' : 'mdi-microphone-off'"
						:class="{ 'nta-recording': audioStore.isRecording }"
						:variant="audioStore.isRecording ? 'tonal' : 'text'"
						size="default"
						:disabled="workspacesStore.isOpenedWorkspaceLocked"
						@click="toggleAudio(sessionId)"
						@mousedown="holdAudio(sessionId)"
					/>
				</template>
			</v-textarea>
		</v-container>
	</v-card>
	<!-- <nta-echo-chamber
		v-if="showEchoChamber"
		:alternisHost="configStore.ALTERNIS_HOST"
		width="560px"
		height="600px"
		teleport="body"
		@close="showEchoChamber = false"
		@microphone-validated="onMicrophoneValidated"
	/> -->
</template>

<script setup lang="ts">
import type { Word } from "@lr-notitia/notitia-alternis/schemas";
import { api } from "@lr-notitia/notitia-alternis/client";
import { ref, computed, onMounted } from "vue";
import { LanguageCode } from "@lr-notitia/notitia-operator/schemas";
import { useDomainStore } from "../../../../store/domain";
import { useSessionStore } from "../../../../store/alternis/session";
import { useAudioStore } from "../../../../store/alternis/audio";
import { useConfigStore } from "../../../../store/alternis/config";
import { useSymbolsStore } from "../../../../store/symbols";
import { useGptSuggestionsStore } from "../../../../store/gpt/suggestions";
import { useInstanceWorkspacesStore } from "../../../../store/i-workspace";
import { UserRole } from "@lr-notitia/notitia-operator/schemas";
import Flag from "./Flag.vue";
import { useResponsiveLayout } from "@lr-notitia/notitia-ui";

defineProps<{
	sessionId: string;
}>();

const domainStore = useDomainStore();
const sessionStore = useSessionStore();
const audioStore = useAudioStore();
const configStore = useConfigStore();
const symbolsStore = useSymbolsStore();
const gptSuggestionsStore = useGptSuggestionsStore();
const workspacesStore = useInstanceWorkspacesStore();

const { displayResponsive } = useResponsiveLayout();

const prevLang = computed(() => sessionStore.users.find((user) => user.userId === domainStore.account.id)?.lang ?? "");
const messageUsePrevLang = ref(true);
const messageText = ref("");
const pressToRecordThrottleTimestamp = ref(Date.now());

// const showEchoChamber = ref(false);

const selectedMicrophoneId = ref<string>();
const availableMicrophones = ref<MediaDeviceInfo[]>([]);

function onEnter(e: KeyboardEvent) {
	if (e.shiftKey) {
		return;
	} else {
		e.preventDefault();
		e.stopPropagation();
		sendMessageText();
	}
}
function sendMessageText() {
	if (!messageText.value) return;
	if (workspacesStore.isOpenedWorkspaceLocked) return;
	const words: Word[] = messageText.value.split(" ").map((word) => ({
		value: word,
		probability: 1,
	}));
	api()
		.message()
		.put(
			{
				words: words,
				lang: messageUsePrevLang.value ? prevLang.value : "",
			},
			sessionStore.sessionId ?? "",
		);
	messageText.value = "";
}

function toggleAudio(sessionId: string) {
	if (configStore.holdToRecord) return;
	if (localStorage.getItem("selectedMicrophoneDeviceId") !== null) {
		audioStore.toggleRecording(
			sessionId,
			domainStore.account?.id || "",
			domainStore.account?.firstname + " " + domainStore.account?.lastname,
		);
	}
	// else {
	// 	showEchoChamber.value = true;
	// }
}

const DEBOUNCE_TIME = 700; // ms
function holdAudio(sessionId: string) {
	if (!configStore.holdToRecord) return;
	if (localStorage.getItem("selectedMicrophoneDeviceId") !== null) {
		if (timeSinceLastBtnPress() > DEBOUNCE_TIME) {
			pressToRecordThrottleTimestamp.value = Date.now();
			toggleRecording(sessionId);
			addEventListener(
				"mouseup",
				() => {
					const timeSincePress = timeSinceLastBtnPress();
					if (timeSincePress > DEBOUNCE_TIME) {
						toggleRecording(sessionId);
					} else {
						setTimeout(() => {
							toggleRecording(sessionId);
						}, DEBOUNCE_TIME - timeSincePress);
					}
				},
				{ once: true },
			);
		}
	}
	// else {
	// 	showEchoChamber.value = true;
	// }
}

async function getAvailableMicrophones() {
	availableMicrophones.value = (await navigator.mediaDevices.enumerateDevices())
		.filter((device) => device && device.label !== "")
		.filter((device) => device.kind === "audioinput")
		.sort((a, b) => (a.label > b.label ? 1 : -1));
	const defaultMic = availableMicrophones.value.find(
		(mic) => mic.label.toLowerCase().includes("default") || mic.deviceId.toLowerCase().includes("default"),
	);
	if (defaultMic) {
		selectMic(defaultMic.deviceId);
	}
}
function selectMic(id: string) {
	selectedMicrophoneId.value = id;
	audioStore.selectedMicrophoneDeviceId = id;
	localStorage.setItem("selectedMicrophoneDeviceId", id);
}

function toggleRecording(sessionId: string) {
	audioStore.toggleRecording(
		sessionId,
		domainStore.account?.id || "",
		domainStore.account?.firstname + " " + domainStore.account?.lastname,
	);
}

function timeSinceLastBtnPress(): number {
	return Date.now() - pressToRecordThrottleTimestamp.value;
}

// function onMicrophoneValidated(data: {
// 	device: { deviceId: string; groupId: string; kind: string; label: string };
// 	lang: LanguageCode;
// 	type: "microphone-validated";
// }) {
// 	audioStore.selectedMicrophoneDeviceId = data.device.deviceId;
// 	showEchoChamber.value = false;
// 	localStorage.setItem("selectedMicrophoneDeviceId", data.device.deviceId);
// }

onMounted(async () => {
	const stream = await navigator.mediaDevices.getUserMedia({ audio: true }).catch((err) => {
		console.error(err);
		return null;
	});

	await getAvailableMicrophones();

	// Get the device ID from the stream the user actually selected in the browser dialog
	if (stream) {
		const audioTrack = stream.getAudioTracks()[0];
		const settings = audioTrack.getSettings();
		if (settings.deviceId) {
			selectMic(settings.deviceId);
		}
		// Stop the stream since we only needed it to get the device ID
		stream.getTracks().forEach((track) => track.stop());
	}
});
</script>

<style scoped lang="scss">
.nta-recording {
	animation: nta-recording 1s infinite;
}
@keyframes nta-recording {
	0%,
	100% {
		color: rgb(var(--v-theme-error));
	}
	70% {
		color: rgba(var(--v-theme-error), 0.5);
	}
}
</style>
